/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faRocket,
  faLightbulb,
  faUsers,
  faCheckCircle,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faUserTie,
  faBullseye,
  faClock,
  faStar,
  faPaperPlane,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { 
  faLinkedin, 
  faTwitter, 
  faFacebook, 
  faInstagram 
} from '@fortawesome/free-brands-svg-icons';
import { Link } from 'react-router-dom';
import { buildApiUrl } from '../config/api';
// import hemantPatelImage from '../assets/Hemant-Patel.jpg'; // Add image file to use real photo
import '../styles/AboutUs.css';

const AboutUs = ({ hideBackButton = false }) => {
  const navigate = useNavigate();
  const [mailingEmail, setMailingEmail] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  // Original team members list (hardcoded)
  const defaultTeamMembers = [
    {
      name: 'Shefali Patel',
      role: 'Managing Director',
      color: 'orange'
    },
    {
      name: 'Hemant Patel',
      role: 'Founder & CEO',
      color: 'teal'
    },
    {
      name: 'Sharon Wazemba',
      role: 'Client Relations Manager',
      color: 'orange'
    },
    {
      name: 'Samantha Mutinda',
      role: 'Business Development Manager',
      color: 'teal'
    },
    {
      name: 'Aashni Patel',
      role: 'Marketing Lead',
      color: 'orange'
    },
    {
      name: 'Sanjay Saini',
      role: 'Chief Technology Officer',
      color: 'teal'
    },
    {
      name: 'Kalpit Patel',
      role: 'AI And Software Development',
      color: 'orange'
    },
    {
      name: 'Ajay Singh',
      role: 'Development Lead',
      color: 'teal'
    },
    {
      name: 'Tatvagna Shah',
      role: 'SAP Consultant',
      color: 'orange'
    }
  ];

  const [teamMembers, setTeamMembers] = useState(defaultTeamMembers);
  const [teamPhotos, setTeamPhotos] = useState({});
  const [loadingTeam, setLoadingTeam] = useState(true);

  useEffect(() => {
    loadTeamMembers();
    // Also ensure default members are in correct order
    setTeamMembers(reorderTeam(defaultTeamMembers));
  }, []);

  const resolveUploadUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) {
      return path;
    }
    const normalized = path.replace(/^\/+/, '');
    
    // Handle team photos specifically
    if (normalized.startsWith('uploads/team_photos/')) {
      const relativePath = normalized.replace(/^uploads\/team_photos\//, '');
      return buildApiUrl(`/api/users/uploads/team_photos/${relativePath}`);
    }
    
    // Handle regular uploads
    if (normalized.startsWith('uploads/')) {
      const relativePath = normalized.replace(/^uploads\//, '');
      return buildApiUrl(`/api/users/uploads/${relativePath}`);
    }
    
    return buildApiUrl(`/${normalized}`);
  };

  const reorderTeam = (members) => {
    // Canonical display order for core team members
    const orderedNames = [
      'Shefali Patel',
      'Hemant Patel',
      'Sharon Wazemba',
      'Samantha Mutinda',
      'Aashni Patel',
      'Sanjay Saini',
      'Kalpit Patel',
      'Ajay Singh',
      'Tatvagna Shah'
    ];

    // Merge members from API with defaults, ensuring each default exists at least once
    const byName = {};
    members.forEach((m) => {
      const key = (m.name || '').toLowerCase();
      if (key && !byName[key]) {
        byName[key] = m;
      }
    });
    defaultTeamMembers.forEach((m) => {
      const key = m.name.toLowerCase();
      if (!byName[key]) {
        byName[key] = m;
      }
    });

    const allMembers = Object.values(byName);
    const usedNames = new Set();
    const ordered = [];

    // First, push members in the fixed canonical order
    orderedNames.forEach((name) => {
      const key = name.toLowerCase();
      const found = allMembers.find(
        (m) => (m.name || '').toLowerCase() === key
      );
      if (found) {
        ordered.push(found);
        usedNames.add(key);
      }
    });

    // Then append any additional members (newly added), preserving their relative order
    allMembers.forEach((m) => {
      const key = (m.name || '').toLowerCase();
      if (!usedNames.has(key)) {
        ordered.push(m);
      }
    });

    return ordered;
  };

  const loadTeamMembers = async () => {
    try {
      const response = await fetch(buildApiUrl('/api/admin/team-members'));
      if (response.ok) {
        const data = await response.json();
        const members = data.teamMembers || [];
        
        // If we have team members from the API, use them
        // Otherwise fall back to default team members
        if (members.length > 0) {
          // Sort by order if available, otherwise maintain API order
          const sortedMembers = [...members].sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return 0;
          });
          
          const normalizedMembers = sortedMembers.map(member => ({
            name: member.name,
            role: member.role,
            color: member.color || 'orange'
          }));

          setTeamMembers(reorderTeam(normalizedMembers));
        }
        
        // Create a map of name -> photoUrl for quick lookup
        const photosMap = {};
        members.forEach(member => {
          if (member.photoUrl) {
            photosMap[member.name] = member.photoUrl;
          }
        });
        setTeamPhotos(photosMap);
      }
    } catch (error) {
      console.error('Error loading team members:', error);
      // On error, keep using defaultTeamMembers
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const handleMailingSubmit = (e) => {
    e.preventDefault();
    console.log('Mailing list:', mailingEmail);
    setMailingEmail('');
  };

  return (
    <div className="our-team-page">
      {!hideBackButton && (
        <button 
          className="back-to-home-btn" 
          onClick={() => navigate('/')}
          aria-label="Back to Homepage"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
          <span>Back to Home</span>
        </button>
      )}

      {/* Hero Section */}
      <section className="our-team-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Our Team</h1>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
        </div>
      </section>

      {/* Strong Team Section */}
      <section className="strong-team-section">
        <div className="section-container">
          <div className="team-intro-layout">
            <div className="team-intro-text">
              <h2 className="what-drives-title">
                <span className="drives-grey">What Drives</span> <span className="drives-green">Us</span>
              </h2>
              <div className="quote-container">
                <div className="quote-mark">&ldquo;</div>
                <p className="team-quote">
                  Learning organizations always look for new ways. They never rest! 
                  They have impeccable zest to optimize all their processes and 
                  endeavor to fill the gaps.
                </p>
              </div>
              <div className="quote-author">
                <p className="author-name">Hemant Patel,</p>
                <p className="author-title">Founder & CEO, Maxpro Infotech Ltd</p>
              </div>
            </div>
          </div>

          <div className="team-values-grid">
            <div className="value-box">
              <div className="value-icon orange-icon">
                <FontAwesomeIcon icon={faUserTie} />
              </div>
              <h3>Professionally trained</h3>
            </div>
            
            <div className="value-box">
              <div className="value-icon teal-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <h3>Team is available 7 days a week</h3>
            </div>
            
            <div className="value-box">
              <div className="value-icon orange-icon">
                <FontAwesomeIcon icon={faBullseye} />
              </div>
              <h3>Focused on achieving results</h3>
            </div>
            
            <div className="value-box">
              <div className="value-icon teal-icon">
                <FontAwesomeIcon icon={faStar} />
              </div>
              <h3>Exceeds our client's expectations</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="team-members-section">
        <div className="section-container">
          <div className="team-header">
            <h2 className="section-title">Dedicated Team</h2>
          </div>

          <div className="team-grid">
            {teamMembers.map((member, index) => {
              const photoUrl = teamPhotos[member.name];
              return (
                <div key={index} className={`team-card ${member.color}`}>
                  <div className="team-card-overlay"></div>
                  <div className="team-avatar">
                    {photoUrl ? (
                      <img 
                        src={resolveUploadUrl(photoUrl)} 
                        alt={member.name}
                        className="team-avatar-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : (
                      <div 
                        className={`avatar-placeholder ${member.color}-avatar`}
                      >
                        <FontAwesomeIcon icon={faUserTie} />
                      </div>
                    )}
                  </div>
                  <div className="team-info">
                    <p className="team-role">{member.role}</p>
                    <h3 className="team-name">{member.name}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mailing List Section */}
      <section className="mailing-section">
        <div className="section-container">
          <h2 className="section-title">Join Our Mailing List</h2>
          <p className="section-subtitle">
            For receiving our news and updates in your inbox directly.
          </p>
          
          <form className="mailing-form" onSubmit={handleMailingSubmit}>
            <input 
              type="email" 
              placeholder="Enter your email address"
              value={mailingEmail}
              onChange={(e) => setMailingEmail(e.target.value)}
              required
            />
            <button type="submit" className="subscribe-btn">
              <FontAwesomeIcon icon={faPaperPlane} />
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-content">
          <div className="footer-section">
            <h3>For Job Seekers</h3>
            <ul>
              <li><Link to="/jobseeker-dashboard">Browse Jobs</Link></li>
              <li><Link to="/companies">Browse Companies</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>For Employers</h3>
            <ul>
              <li><Link to="/signup">Post a Job</Link></li>
              <li><Link to="/pricing">Pricing (Free)</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>About AksharJobs</h3>
            <ul>
              <li><Link to="/about-platform">About Platform</Link></li>
              <li><Link to="/about-name">About Name</Link></li>
              <li><Link to="/contact-us">Contact</Link></li>
              <li><Link to="/blog">Blog</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h3>Support</h3>
            <ul>
              <li><Link to="/help-center">Help Center</Link></li>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/cookie-policy">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>
            <span>&copy; 2026 Maxpro Infotech Limited. All rights reserved. | Connecting talent with opportunity.</span>
            <span className="footer-social-icons">
              <a href="https://linkedin.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FontAwesomeIcon icon={faLinkedin} />
              </a>
              <a href="https://twitter.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FontAwesomeIcon icon={faTwitter} />
              </a>
              <a href="https://facebook.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FontAwesomeIcon icon={faFacebook} />
              </a>
              <a href="https://instagram.com" className="social-icon-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FontAwesomeIcon icon={faInstagram} />
              </a>
            </span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
